// src/components/TileBuilder/ChartControls/LineChartSpecificControls.tsx
import React from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Typography,
} from '@mui/material';
import { setSettings } from '../../../../support/slices/tileBuilderSlice';
import { ChartControlProps, LINE_CURVES, SCALE_TYPES, SLICE_OPTIONS } from './ChartControlProps';
import { TileSettings } from '../../../../types/tileBuilderTypes';

const LineChartSpecificControls: React.FC<ChartControlProps> = ({ chartId, settings, dispatch }) => {
  const s: any = settings as any;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
      <Typography variant="subtitle2" sx={{ width: '100%' }}>
        Line Chart
      </Typography>

      <FormControl sx={{ minWidth: 180 }} size="small">
        <InputLabel>Curve</InputLabel>
        <Select
          label="Curve"
          value={s.lineCurve ?? 'linear'}
          onChange={(e) =>
            dispatch(setSettings({ chartId, settings: { lineCurve: e.target.value as TileSettings['lineCurve'] } }))
          }
        >
          {LINE_CURVES.map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={{ minWidth: 180 }} size="small">
        <InputLabel>X Scale Type</InputLabel>
        <Select
          label="X Scale Type"
          value={s.xScaleType ?? 'point'}
          onChange={(e) =>
            dispatch(setSettings({ chartId, settings: { xScaleType: e.target.value as TileSettings['xScaleType'] } }))
          }
        >
          {SCALE_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={{ minWidth: 180 }} size="small">
        <InputLabel>Y Scale Type</InputLabel>
        <Select
          label="Y Scale Type"
          value={s.yScaleType ?? 'linear'}
          onChange={(e) =>
            dispatch(setSettings({ chartId, settings: { yScaleType: e.target.value as TileSettings['yScaleType'] } }))
          }
        >
          {SCALE_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Y Scale Min"
        size="small"
        value={s.yScaleMin ?? 'auto'}
        onChange={(e) =>
          dispatch(
            setSettings({
              chartId,
              settings: { yScaleMin: e.target.value === 'auto' ? 'auto' : Number(e.target.value) },
            })
          )
        }
        sx={{ width: 160 }}
        placeholder="auto or number"
      />

      <TextField
        label="Y Scale Max"
        size="small"
        value={s.yScaleMax ?? 'auto'}
        onChange={(e) =>
          dispatch(
            setSettings({
              chartId,
              settings: { yScaleMax: e.target.value === 'auto' ? 'auto' : Number(e.target.value) },
            })
          )
        }
        sx={{ width: 160 }}
        placeholder="auto or number"
      />

      <FormControlLabel
        control={
          <Switch
            checked={!!s.yScaleStacked}
            onChange={(e) => dispatch(setSettings({ chartId, settings: { yScaleStacked: e.target.checked } }))}
          />
        }
        label="Y Stacked"
      />

      <FormControlLabel
        control={
          <Switch
            checked={!!s.yScaleReverse}
            onChange={(e) => dispatch(setSettings({ chartId, settings: { yScaleReverse: e.target.checked } }))}
          />
        }
        label="Y Reverse"
      />

      <FormControl sx={{ minWidth: 180 }} size="small">
        <InputLabel>Slices</InputLabel>
        <Select
          label="Slices"
          value={s.enableSlices === false ? '' : s.enableSlices ?? 'x'}
          onChange={(e) =>
            dispatch(
              setSettings({
                chartId,
                settings: { enableSlices: e.target.value === '' ? false : (e.target.value as 'x' | 'y') },
              })
            )
          }
        >
          {SLICE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControlLabel
        control={
          <Switch checked={s.useMesh !== false} onChange={(e) => dispatch(setSettings({ chartId, settings: { useMesh: e.target.checked } }))} />
        }
        label="Use Mesh"
      />

      <FormControlLabel
        control={
          <Switch checked={!!s.enableArea} onChange={(e) => dispatch(setSettings({ chartId, settings: { enableArea: e.target.checked } }))} />
        }
        label="Enable Area"
      />

      {s.enableArea && (
        <>
          <TextField
            label="Area Opacity"
            type="number"
            size="small"
            value={s.areaOpacity ?? 0.35}
            onChange={(e) => dispatch(setSettings({ chartId, settings: { areaOpacity: Number(e.target.value) } }))}
            sx={{ width: 160 }}
            inputProps={{ step: 0.01, min: 0, max: 1 }}
          />
          <TextField
            label="Area Baseline"
            type="number"
            size="small"
            value={s.areaBaselineValue ?? 0}
            onChange={(e) => dispatch(setSettings({ chartId, settings: { areaBaselineValue: Number(e.target.value) } }))}
            sx={{ width: 160 }}
          />
        </>
      )}

      <TextField
        label="Truncate X Ticks At"
        type="number"
        size="small"
        value={s.truncateTickAt ?? 0}
        onChange={(e) => dispatch(setSettings({ chartId, settings: { truncateTickAt: Number(e.target.value) } }))}
        sx={{ width: 200 }}
        helperText="0 = no truncation"
      />

      <FormControlLabel
        control={
          <Switch
            checked={!!s.enablePointLabel}
            onChange={(e) => dispatch(setSettings({ chartId, settings: { enablePointLabel: e.target.checked } }))}
          />
        }
        label="Point Labels"
      />

      {s.enablePointLabel && (
        <TextField
          label="Point Label Y Offset"
          type="number"
          size="small"
          value={s.pointLabelYOffset ?? 0}
          onChange={(e) => dispatch(setSettings({ chartId, settings: { pointLabelYOffset: Number(e.target.value) } }))}
          sx={{ width: 200 }}
        />
      )}

      <TextField
        label="Point Size"
        type="number"
        size="small"
        value={s.pointSize ?? 6}
        onChange={(e) => dispatch(setSettings({ chartId, settings: { pointSize: Number(e.target.value) } }))}
        sx={{ width: 160 }}
        inputProps={{ min: 0 }}
      />
      <TextField
        label="Point Border Width"
        type="number"
        size="small"
        value={s.pointBorderWidth ?? 1}
        onChange={(e) => dispatch(setSettings({ chartId, settings: { pointBorderWidth: Number(e.target.value) } }))}
        sx={{ width: 190 }}
        inputProps={{ min: 0 }}
      />
      <TextField
        label="Point Border Color"
        size="small"
        value={s.pointBorderColor ?? '#fff'}
        onChange={(e) => dispatch(setSettings({ chartId, settings: { pointBorderColor: e.target.value } }))}
        sx={{ width: 200 }}
      />
      <TextField
        label="Point Color"
        size="small"
        value={s.pointColor ?? ''}
        onChange={(e) => dispatch(setSettings({ chartId, settings: { pointColor: e.target.value } }))}
        sx={{ width: 200 }}
      />
    </Box>
  );
};

export default LineChartSpecificControls;